/* ==========================================================================
   Address Fields Component
   Reusable address field group with autocomplete and verification
   Task F38: Address autocomplete component + integration
   PORTAL-161: country-aware labels, region options and postal-code rules.
   ========================================================================== */

import { useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import type { AxiosInstance } from 'axios'
import { CheckCircle2, AlertTriangle } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Combobox } from '@/components/ui/Combobox'
import { FormField } from '@/components/ui/FormField'
import { AddressAutocomplete } from './AddressAutocomplete'
import { verifyAddress, type AddressSuggestion } from '@/api/address'
import {
  DEFAULT_COUNTRY,
  formatPostalCode,
  isUSStateCountry,
  postalLabel,
  postalPlaceholder,
  regionLabel,
  regionOptions,
  regionPlaceholder,
} from '@/utils/countries'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface AddressFieldsControlledProps {
  /** Controlled mode — pass values and change handlers directly */
  street: string
  city: string
  state: string | null
  zip: string
  onStreetChange: (v: string) => void
  onCityChange: (v: string) => void
  onStateChange: (v: string | null) => void
  onZipChange: (v: string) => void
  formMode?: false
  fieldPrefix?: never
}

interface AddressFieldsFormProps {
  /** react-hook-form mode — reads/writes via useFormContext */
  formMode: true
  fieldPrefix: string // e.g. "patient_address_"
  street?: never
  city?: never
  state?: never
  zip?: never
  onStreetChange?: never
  onCityChange?: never
  onStateChange?: never
  onZipChange?: never
}

type AddressFieldsModeProps = AddressFieldsControlledProps | AddressFieldsFormProps

type AddressFieldsProps = AddressFieldsModeProps & {
  /** Verification status: null = not checked, true/false = result */
  verified?: boolean | null
  /** Called when verification completes */
  onVerified?: (verified: boolean) => void
  disabled?: boolean
  errors?: { street?: string; city?: string; state?: string; zip?: string }
  touched?: Record<string, boolean>
  onBlur?: (field: string) => void
  required?: boolean
  /** Axios instance — pass adminClient for admin context */
  apiClient?: AxiosInstance
  /** Optional heading text */
  heading?: string
  /**
   * ISO-3166 alpha-2 country the address belongs to (PORTAL-161). Drives the
   * State/Province label and options, the ZIP/Postal Code label and format,
   * and whether USPS autocomplete/verification is offered (US only).
   */
  country?: string
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AddressFields(props: AddressFieldsProps) {
  const {
    verified = null,
    onVerified,
    disabled = false,
    errors,
    touched,
    onBlur,
    required = false,
    apiClient,
    heading,
    country = DEFAULT_COUNTRY,
  } = props

  const [verifying, setVerifying] = useState(false)

  // USPS autocomplete + verification only make sense for US addresses.
  const isUS = isUSStateCountry(country)
  const regionOpts = regionOptions(country)

  // react-hook-form context (only used in formMode)
  const formContext = props.formMode ? useFormContext() : null
  const formWatch = formContext?.watch
  const formSetValue = formContext?.setValue

  // Read values depending on mode
  const streetValue = props.formMode
    ? (formWatch?.(`${props.fieldPrefix}street`) as string) || ''
    : props.street
  const cityValue = props.formMode
    ? (formWatch?.(`${props.fieldPrefix}city`) as string) || ''
    : props.city
  const stateValue = props.formMode
    ? (formWatch?.(`${props.fieldPrefix}state`) as string) || null
    : props.state
  const zipValue = props.formMode
    ? (formWatch?.(`${props.fieldPrefix}zip`) as string) || ''
    : props.zip

  // Set values depending on mode
  const setStreet = useCallback(
    (v: string) => {
      if (props.formMode) {
        formSetValue?.(`${props.fieldPrefix}street`, v)
      } else {
        props.onStreetChange(v)
      }
    },
    [props.formMode, formSetValue, props.formMode ? props.fieldPrefix : undefined, props.formMode ? undefined : props.onStreetChange]
  )

  const setCity = useCallback(
    (v: string) => {
      if (props.formMode) {
        formSetValue?.(`${props.fieldPrefix}city`, v)
      } else {
        props.onCityChange(v)
      }
    },
    [props.formMode, formSetValue, props.formMode ? props.fieldPrefix : undefined, props.formMode ? undefined : props.onCityChange]
  )

  const setStateVal = useCallback(
    (v: string | null) => {
      if (props.formMode) {
        formSetValue?.(`${props.fieldPrefix}state`, v || '')
      } else {
        props.onStateChange(v)
      }
    },
    [props.formMode, formSetValue, props.formMode ? props.fieldPrefix : undefined, props.formMode ? undefined : props.onStateChange]
  )

  const setZip = useCallback(
    (v: string) => {
      if (props.formMode) {
        formSetValue?.(`${props.fieldPrefix}zip`, v)
      } else {
        props.onZipChange(v)
      }
    },
    [props.formMode, formSetValue, props.formMode ? props.fieldPrefix : undefined, props.formMode ? undefined : props.onZipChange]
  )

  // Run verification against the API (US addresses only — the verifier is USPS-backed)
  const runVerification = useCallback(
    async (street: string, city: string, state: string, zip: string) => {
      if (!isUS) return
      if (!street || !city || !state || !zip) return
      // State must be 2 uppercase letters, zip must be valid
      if (!/^[A-Z]{2}$/.test(state)) return
      if (!/^\d{5}(-\d{4})?$/.test(zip)) return

      setVerifying(true)
      try {
        const result = apiClient
          ? await verifyAddress({ street, city, state, zipcode: zip }, apiClient)
          : await verifyAddress({ street, city, state, zipcode: zip })
        onVerified?.(result.verified)
      } catch {
        // Silently fail — don't break the form
      } finally {
        setVerifying(false)
      }
    },
    [apiClient, onVerified, isUS]
  )

  // Handle autocomplete suggestion selection
  function handleAddressSelect(suggestion: AddressSuggestion) {
    setStreet(suggestion.street)
    setCity(suggestion.city)
    setStateVal(suggestion.state)
    setZip(suggestion.zipcode)
    // Auto-verify after selection
    runVerification(suggestion.street, suggestion.city, suggestion.state, suggestion.zipcode)
  }

  // Handle street blur — verify if all fields are populated
  function handleStreetBlur() {
    onBlur?.('street')
    // If all fields are filled and we haven't verified, trigger verification
    if (streetValue && cityValue && stateValue && zipValue && verified === null) {
      runVerification(streetValue, cityValue, stateValue, zipValue)
    }
  }

  const streetError = touched?.street ? errors?.street : undefined
  const cityError = touched?.city ? errors?.city : undefined
  const stateError = touched?.state ? errors?.state : undefined
  const zipError = touched?.zip ? errors?.zip : undefined

  return (
    <div className="space-y-5">
      {/* Heading with verification badge (verification is US-only) */}
      {heading !== undefined && (
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-gray-700">{heading}</h3>
          {isUS && verifying && (
            <span className="text-xs text-[var(--color-gray-500)]">Verifying...</span>
          )}
          {isUS && !verifying && verified === true && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Verified
            </span>
          )}
          {isUS && !verifying && verified === false && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              Unverified
            </span>
          )}
        </div>
      )}

      {/* Street — USPS autocomplete for US, plain input elsewhere */}
      <FormField
        label="Street Address"
        required={required}
        error={streetError}
        htmlFor="address-street"
      >
        {isUS ? (
          <AddressAutocomplete
            id="address-street"
            value={streetValue}
            onChange={setStreet}
            onAddressSelect={handleAddressSelect}
            disabled={disabled}
            error={!!streetError}
            onBlur={handleStreetBlur}
            apiClient={apiClient}
          />
        ) : (
          <Input
            id="address-street"
            type="text"
            placeholder="Street address"
            value={streetValue}
            onChange={(e) => setStreet(e.target.value)}
            onBlur={() => onBlur?.('street')}
            error={!!streetError}
            disabled={disabled}
            maxLength={255}
          />
        )}
      </FormField>

      {/* City, State/Province, ZIP/Postal Code row */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
        {/* City */}
        <div className="sm:col-span-2">
          <FormField
            label="City"
            required={required}
            error={cityError}
            htmlFor="address-city"
          >
            <Input
              id="address-city"
              type="text"
              placeholder="City"
              value={cityValue}
              onChange={(e) => setCity(e.target.value)}
              onBlur={() => onBlur?.('city')}
              error={!!cityError}
              disabled={disabled}
            />
          </FormField>
        </div>

        {/* State / Province */}
        <div className="sm:col-span-2">
          <FormField
            label={regionLabel(country)}
            required={required}
            error={stateError}
            htmlFor="address-state"
          >
            {regionOpts ? (
              <Combobox
                options={regionOpts}
                value={stateValue}
                onChange={(v) => setStateVal(v)}
                placeholder={regionLabel(country)}
                error={!!stateError}
                disabled={disabled}
                clearable={false}
              />
            ) : (
              <Input
                id="address-state"
                type="text"
                placeholder={regionPlaceholder(country)}
                value={stateValue ?? ''}
                onChange={(e) => setStateVal(e.target.value)}
                onBlur={() => onBlur?.('state')}
                error={!!stateError}
                disabled={disabled}
                maxLength={100}
              />
            )}
          </FormField>
        </div>

        {/* ZIP / Postal Code */}
        <div className="sm:col-span-2">
          <FormField
            label={postalLabel(country)}
            required={required}
            error={zipError}
            htmlFor="address-zip"
          >
            <Input
              id="address-zip"
              type="text"
              inputMode={isUS ? 'numeric' : 'text'}
              placeholder={postalPlaceholder(country)}
              value={formatPostalCode(country, zipValue)}
              onChange={(e) => setZip(formatPostalCode(country, e.target.value))}
              onBlur={() => onBlur?.('zip')}
              error={!!zipError}
              disabled={disabled}
              maxLength={10}
            />
          </FormField>
        </div>
      </div>
    </div>
  )
}

export default AddressFields
